import React, { useEffect, useRef, useState } from 'react'
import { CaretDownOutlined, CloseOutlined } from '@ant-design/icons'
import moment from 'moment'
import DateRangePresetPicker, { DateRangeValue } from './DateRangePresetPicker'
import {
	MANAGER_TASK_EVALUATION_STATUS,
	MANAGER_TASK_WORKFLOW_STATUS,
	ManagerTaskUser
} from './types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TaskFilters {
	statuses: MANAGER_TASK_WORKFLOW_STATUS[]
	evaluations: MANAGER_TASK_EVALUATION_STATUS[]
	assigneeId: number | null
	dateRange: DateRangeValue | null
}

export const EMPTY_TASK_FILTERS: TaskFilters = {
	statuses: [],
	evaluations: [],
	assigneeId: null,
	dateRange: null
}

interface TaskFilterBarProps {
	filters: TaskFilters
	onChange: (f: TaskFilters) => void
	assignees: ManagerTaskUser[]
	currentUserId?: number
}

// ---------------------------------------------------------------------------
// Shared pill styles
// ---------------------------------------------------------------------------

const basePill: React.CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 5,
	padding: '4px 10px',
	border: '1px solid #d1d5db',
	borderRadius: 6,
	background: '#fff',
	cursor: 'pointer',
	fontSize: 12.5,
	color: '#374151',
	whiteSpace: 'nowrap',
	lineHeight: '18px'
}

const activePill: React.CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 5,
	padding: '4px 10px',
	border: '1px solid #93c5fd',
	borderRadius: 6,
	background: '#eff6ff',
	cursor: 'pointer',
	fontSize: 12.5,
	color: '#1d4ed8',
	whiteSpace: 'nowrap',
	lineHeight: '18px'
}

// ---------------------------------------------------------------------------
// DropdownPortal — fixed-position dropdown that escapes Drawer overflow
// ---------------------------------------------------------------------------

interface DropdownPortalProps {
	anchorRef: React.RefObject<HTMLButtonElement>
	dropdownRef: React.RefObject<HTMLDivElement>
	open: boolean
	children: React.ReactNode
}

function DropdownPortal({
	anchorRef, dropdownRef, open, children
}: DropdownPortalProps) {
	const [pos, setPos] = useState<React.CSSProperties>({})

	useEffect(() => {
		if (!open || !anchorRef.current) return
		const rect = anchorRef.current.getBoundingClientRect()
		setPos({
			position: 'fixed',
			top: rect.bottom + 4,
			left: rect.left,
			zIndex: 9999
		})
	}, [open, anchorRef])

	if (!open) return null

	return (
		<div ref={dropdownRef} style={pos}>
			{children}
		</div>
	)
}

// ---------------------------------------------------------------------------
// Shared checkbox item inside dropdowns
// ---------------------------------------------------------------------------

interface CheckboxItemProps {
	checked: boolean
	label: string
	onClick: () => void
}

function CheckboxItem({ checked, label, onClick }: CheckboxItemProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 10,
				width: '100%',
				padding: '7px 14px',
				background: 'transparent',
				border: 'none',
				cursor: 'pointer',
				fontSize: 13,
				color: '#374151',
				textAlign: 'left'
			}}
		>
			<span style={{
				width: 16,
				height: 16,
				borderRadius: 3,
				border: checked ? 'none' : '1.5px solid #d1d5db',
				background: checked ? '#1d4ed8' : '#fff',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flexShrink: 0
			}}>
				{checked && (
					<svg width="10" height="8" viewBox="0 0 10 8" fill="none">
						<path d="M1 3.5L3.5 6L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				)}
			</span>
			{label}
		</button>
	)
}

// ---------------------------------------------------------------------------
// MultiSelectPill — Stav, Výsledok
// ---------------------------------------------------------------------------

interface MultiSelectOption<T> {
	key: T
	label: string
}

interface MultiSelectPillProps<T extends string> {
	label: string
	value: T[]
	options: MultiSelectOption<T>[]
	onChange: (v: T[]) => void
}

function MultiSelectPill<T extends string>({
	label, value, options, onChange
}: MultiSelectPillProps<T>) {
	const [open, setOpen] = useState(false)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!open) return undefined
		const handler = (e: MouseEvent) => {
			const t = e.target as Node
			const inTrigger = triggerRef.current && triggerRef.current.contains(t)
			const inDropdown = dropdownRef.current && dropdownRef.current.contains(t)
			if (!inTrigger && !inDropdown) setOpen(false)
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [open])

	const isActive = value.length > 0

	const activeLabel = value
		.map((v) => {
			const opt = options.find((o) => o.key === v)
			return opt ? opt.label : v
		})
		.join(', ')

	const handleToggle = (key: T) => {
		if (value.includes(key)) {
			onChange(value.filter((v) => v !== key))
		} else {
			onChange([...value, key])
		}
	}

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				onClick={() => setOpen((v) => !v)}
				style={isActive ? activePill : basePill}
			>
				<span style={{ fontWeight: isActive ? 600 : 400 }}>
					{isActive ? `${label}: ${activeLabel}` : label}
				</span>
				{isActive
					? (
						<CloseOutlined
							style={{ fontSize: 10 }}
							onClick={(e) => { e.stopPropagation(); onChange([]) }}
						/>
					)
					: <CaretDownOutlined style={{ fontSize: 10, color: '#9ca3af' }} />}
			</button>
			<DropdownPortal anchorRef={triggerRef} dropdownRef={dropdownRef} open={open}>
				<div style={{
					background: '#fff',
					border: '1px solid #e5e7eb',
					borderRadius: 8,
					boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
					minWidth: 200,
					padding: '6px 0'
				}}>
					{options.map((opt) => (
						<CheckboxItem
							key={opt.key}
							checked={value.includes(opt.key)}
							label={opt.label}
							onClick={() => handleToggle(opt.key)}
						/>
					))}
				</div>
			</DropdownPortal>
		</>
	)
}

// ---------------------------------------------------------------------------
// SingleSelectPill — Zodpovedný
// ---------------------------------------------------------------------------

interface SingleSelectPillProps {
	label: string
	value: number | null
	options: { id: number; name: string }[]
	onChange: (v: number | null) => void
}

function SingleSelectPill({
	label, value, options, onChange
}: SingleSelectPillProps) {
	const [open, setOpen] = useState(false)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!open) return undefined
		const handler = (e: MouseEvent) => {
			const t = e.target as Node
			const inTrigger = triggerRef.current && triggerRef.current.contains(t)
			const inDropdown = dropdownRef.current && dropdownRef.current.contains(t)
			if (!inTrigger && !inDropdown) setOpen(false)
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [open])

	const isActive = value !== null
	const found = options.find((o) => o.id === value)
	const activeLabel = found ? found.name : ''

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				onClick={() => setOpen((v) => !v)}
				style={isActive ? activePill : basePill}
			>
				<span style={{ fontWeight: isActive ? 600 : 400 }}>
					{isActive ? `${label}: ${activeLabel}` : label}
				</span>
				{isActive
					? (
						<CloseOutlined
							style={{ fontSize: 10 }}
							onClick={(e) => { e.stopPropagation(); onChange(null) }}
						/>
					)
					: <CaretDownOutlined style={{ fontSize: 10, color: '#9ca3af' }} />}
			</button>
			<DropdownPortal anchorRef={triggerRef} dropdownRef={dropdownRef} open={open}>
				<div style={{
					background: '#fff',
					border: '1px solid #e5e7eb',
					borderRadius: 8,
					boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
					minWidth: 180,
					maxHeight: 240,
					overflowY: 'auto',
					padding: '6px 0'
				}}>
					{options.length === 0
						? (
							<div style={{ padding: '10px 14px', fontSize: 13, color: '#9ca3af' }}>
								Žiadne možnosti
							</div>
						)
						: options.map((opt) => {
							const selected = value === opt.id
							return (
								<button
									key={opt.id}
									type="button"
									onClick={() => { onChange(selected ? null : opt.id); setOpen(false) }}
									style={{
										display: 'block',
										width: '100%',
										padding: '7px 14px',
										textAlign: 'left',
										background: selected ? '#eff6ff' : 'transparent',
										border: 'none',
										cursor: 'pointer',
										fontSize: 13,
										color: selected ? '#1d4ed8' : '#374151',
										fontWeight: selected ? 600 : 400
									}}
								>
									{opt.name}
								</button>
							)
						})}
				</div>
			</DropdownPortal>
		</>
	)
}

// ---------------------------------------------------------------------------
// TermínPill — activatable date range filter
// ---------------------------------------------------------------------------

function getDefaultDateRange(): DateRangeValue {
	return [
		moment().subtract(27, 'days').startOf('day'),
		moment().endOf('day')
	]
}

interface TermínPillProps {
	value: DateRangeValue | null
	onChange: (v: DateRangeValue | null) => void
}

function TermínPill({ value, onChange }: TermínPillProps) {
	if (value === null) {
		return (
			<button
				type="button"
				onClick={() => onChange(getDefaultDateRange())}
				style={basePill}
			>
				<span>Termín</span>
				<CaretDownOutlined style={{ fontSize: 10, color: '#9ca3af' }} />
			</button>
		)
	}

	return (
		<DateRangePresetPicker
			value={value}
			onChange={(range) => onChange(range)}
			onClear={() => onChange(null)}
		/>
	)
}

// ---------------------------------------------------------------------------
// TaskFilterBar — the main exported component
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
	{ key: MANAGER_TASK_WORKFLOW_STATUS.TODO, label: 'Na spracovanie' },
	{ key: MANAGER_TASK_WORKFLOW_STATUS.IN_PROGRESS, label: 'Rozpracované' },
	{ key: MANAGER_TASK_WORKFLOW_STATUS.DONE, label: 'Hotové' },
	{ key: MANAGER_TASK_WORKFLOW_STATUS.VOIDED, label: 'Zrušené' }
]

const EVALUATION_OPTIONS = [
	{ key: MANAGER_TASK_EVALUATION_STATUS.PENDING, label: 'Čaká' },
	{ key: MANAGER_TASK_EVALUATION_STATUS.SUCCESS, label: 'Včas' },
	{ key: MANAGER_TASK_EVALUATION_STATUS.FAILED, label: 'Po termíne' }
]

function nameFromUser(u: ManagerTaskUser): string {
	const full = [u.firstName, u.lastName].filter(Boolean).join(' ')
	return full || u.email || `#${u.id}`
}

function TaskFilterBar({ filters, onChange, assignees, currentUserId }: TaskFilterBarProps) {
	const assigneeOptions = assignees.map((u) => ({ id: u.id, name: nameFromUser(u) }))
	const myTasksActive = !!(currentUserId && filters.assigneeId === currentUserId)

	const toggleMyTasks = () => {
		if (myTasksActive) {
			onChange({ ...filters, assigneeId: null })
		} else if (currentUserId) {
			onChange({ ...filters, assigneeId: currentUserId })
		}
	}

	return (
		<div style={{
			display: 'flex',
			gap: 8,
			flexWrap: 'wrap',
			alignItems: 'center'
		}}>
			{currentUserId && (
				<button
					type="button"
					onClick={toggleMyTasks}
					style={myTasksActive ? activePill : basePill}
				>
					<span style={{ fontWeight: myTasksActive ? 600 : 400 }}>Moje</span>
					{myTasksActive && (
						<CloseOutlined
							style={{ fontSize: 10 }}
							onClick={(e) => { e.stopPropagation(); onChange({ ...filters, assigneeId: null }) }}
						/>
					)}
				</button>
			)}
			<SingleSelectPill
				label="Zodpovedný"
				value={filters.assigneeId}
				options={assigneeOptions}
				onChange={(v) => onChange({ ...filters, assigneeId: v })}
			/>
			<MultiSelectPill<MANAGER_TASK_WORKFLOW_STATUS>
				label="Stav"
				value={filters.statuses}
				options={STATUS_OPTIONS}
				onChange={(v) => onChange({ ...filters, statuses: v })}
			/>
			<TermínPill
				value={filters.dateRange}
				onChange={(v) => onChange({ ...filters, dateRange: v })}
			/>
		</div>
	)
}

export default TaskFilterBar
